// @ts-ignore
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWrapper } from '../../helpers';
import PostContentEditor from '../../components/Editors/PostContentEditor/PostContentEditor';
import LoadingOverlay from '../../components/Overlays/LoadingOverlay'; // kept
import PostDistributionForm from '../../components/Forms/PostDistributionForm';
import { useDispatch, useSelector } from 'react-redux';
import { distActions } from '../../store';
import { useTranslation } from 'react-i18next';
import { ImeanHeader } from '../../components/Header/Header';
import { StickyNavigationHeader } from '../../components/Navigation/StickyNavigationHeader';
import AutopublicationCard from '../../components/Cards/AutopublicationCard';
import DistCentralizedMediaStorage from '../../components/Cards/MediaCardSelfContainted';
import ImageGeneratorCard from '../../components/Cards/ImageGeneratorCard';

// NEW: antd UI (visual-only redesign; all business logic preserved)
import {
	Layout,
	Row,
	Col,
	Card,
	Button,
	Divider,
	Space,
	Typography,
	List,
	Badge,
	Progress,
	Grid,
	message,
	Tooltip,
	Alert,
	Collapse,
} from 'antd';
import {
	ThunderboltOutlined,
	PlayCircleOutlined,
	HourglassOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import MediaCardSelfContained from '../../components/Cards/MediaCardSelfContainted';

const baseUrl = `${process.env.REACT_APP_API_URL}`;

const STATUS_META = {
	queued: {
		text: 'postDistribution.statusMeta.queued',
		status: 'processing',
		icon: <HourglassOutlined />,
	},
	active: {
		text: 'postDistribution.statusMeta.active',
		status: 'processing',
		icon: <HourglassOutlined />,
	},
	delayed: {
		text: 'postDistribution.statusMeta.delayed',
		status: 'warning',
		icon: <HourglassOutlined />,
	},
	completed: {
		text: 'postDistribution.statusMeta.completed',
		status: 'success',
		icon: <CheckCircleOutlined />,
	},
	failed: {
		text: 'postDistribution.statusMeta.failed',
		status: 'error',
		icon: <CloseCircleOutlined />,
	},
};

/**
 * @function DistributePostPage
 * @description This component is responsible for distributing posts to selected employees.
 * It includes a form for setting up the distribution, controls for generating posts,
 * a progress bar to track the generation status, and a post editor.
 *
 * @returns {JSX.Element} The DistributePostPage component.
 *
 * @example
 * // Usage:
 * <DistributePostPage />
 */
// yo hey there
const DistributePostPage = () => {
	const { t } = useTranslation();

	// ------------------ Redux state (kept) ------------------
	const form = useSelector((state) => state.dist.form);
	const selectedEmployees = useSelector((state) => state.dist.selected_employees);
	const jobStatus = useSelector((state) => state.dist.jobStatus);
	const autopublishByEmployee = useSelector((state) => state.dist.autopublishByEmployee || {});

	// ------------------ Local UI state (kept) ------------------
	const [isGenerated, setIsGenerated] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [generatedPosts, setGeneratedPosts] = useState([]);
	const [loading, setLoading] = useState(false);

	// Timer (kept)
	const [elapsedTime, setElapsedTime] = useState(0);
	const [lastGenerationTime, setLastGenerationTime] = useState(null);

	// Router & dispatch
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const screens = Grid.useBreakpoint();

	// ------------------ Effects (kept) ------------------
	useEffect(() => {
		// Load employees' social media on mount
		dispatch(distActions.fetchEmployeeSocialMedia());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Derived: whether any jobs are active
	const hasActiveJobs = useCallback(() => {
		return Object.values(jobStatus).some(
			(job) => job?.status === 'queued' || job?.status === 'active' || job?.status === 'delayed',
		);
	}, [jobStatus]);

	// Timer start/stop — logic preserved, implemented with memoized active flag
	const activeNow = useMemo(() => isGenerating || hasActiveJobs(), [isGenerating, hasActiveJobs]);

	useEffect(() => {
		let interval;
		if (activeNow) {
			interval = setInterval(() => setElapsedTime((prev) => prev + 1), 1000);
		} else {
			if (elapsedTime > 0) setLastGenerationTime(elapsedTime);
			setElapsedTime(0);
		}
		return () => clearInterval(interval);
	}, [activeNow]); // eslint-disable-line react-hooks/exhaustive-deps

	const formatTime = (secs) => {
		const minutes = Math.floor(secs / 60);
		const seconds = secs % 60;
		return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
	};

	// ------------------ Polling (kept) ------------------
	const stopIfNoActive = () => {
		// micro delay to make sure redux has updated statuses
		setTimeout(() => {
			if (!hasActiveJobs()) setIsGenerating(false);
		}, 100);
	};

	const startPollingJob = (emp, jobId) => {
		const interval = setInterval(async () => {
			try {
				const jobRes = await fetchWrapper.get(`${baseUrl}/dist/job/${jobId}`);
				// console.log(`Polling for ${emp.fullName} → ${jobRes.state}`);

				if (jobRes?.state === 'completed') {
					clearInterval(interval);
					dispatch(
						distActions.setJobStatus({
							employeeId: emp.id,
							jobId,
							status: 'completed',
							result: {
								result: jobRes.result,
								id: emp.id,
								UserId: emp.id,
							},
						}),
					);
					setGeneratedPosts((prev) => [...prev, { ...jobRes.result, userId: emp.id }]);
					stopIfNoActive();
				} else if (jobRes?.state === 'failed') {
					clearInterval(interval);
					let parsedResult = null;
					try {
						parsedResult =
							typeof jobRes.result === 'string' ? JSON.parse(jobRes.result) : jobRes.result;
					} catch (e) {
						parsedResult = null;
					}

					dispatch(
						distActions.setJobStatus({
							employeeId: emp.id,
							jobId,
							status: 'failed',
							result: {
								...(parsedResult || {}),
								error: 'Post generation failed',
								id: emp.id,
								UserId: emp.id,
							},
						}),
					);
					stopIfNoActive();
				} else if (jobRes?.state === 'active') {
					dispatch(
						distActions.setJobStatus({
							employeeId: emp.id,
							jobId,
							status: 'active',
						}),
					);
				}
			} catch (e) {
				clearInterval(interval);
				console.error(`Polling error for job ${jobId}`, e);
				dispatch(distActions.setJobStatus({ employeeId: emp.id, status: 'failed' }));
				stopIfNoActive();
			}
		}, 3000);
	};

	// ------------------ Generate (kept) ------------------
	const handleGenerateWithPolling = async () => {
		if (selectedEmployees.length === 0) {
			message.warning(
				t('postDistribution.validation.selectEmployees') || 'Please select employees',
			);
			return;
		}

		const { title, instruction, contentType, postText, postLink, languages } = form;
		const content = contentType === 0 ? postLink : postText;

		if (!content?.trim()) {
			message.error(
				t('postDistribution.validation.provideContent') ||
					'Please provide content in the selected tab (Text or Link)',
			);
			return;
		}

		// console.log('📝 Submitting post generation with:', {
		// 	title,
		// 	instruction,
		// 	content,
		// 	contentType,
		// 	model: 'gpt-4o-mini',
		// 	temperature: 0.75,
		// 	employees: selectedEmployees.map((e) => e.id),
		// 	languages,
		// });

		// Reset previous generation data for new generation (kept)
		dispatch(distActions.resetJobStatus());
		setGeneratedPosts([]);
		setIsGenerated(false);
		setIsGenerating(true);

		for (const emp of selectedEmployees) {
			dispatch(distActions.clearModeratorPost({ employeeId: emp.id }));
			try {
				const res = await fetchWrapper.post(`${baseUrl}/dist/generate-test-post`, {
					userId: emp.id,
					title,
					article: content,
					instruction: form.instruction,
					model: 'gpt-4o-mini',
					temperature: 0.75,
					contentType: contentType === 0 ? 'link' : 'text',
					languages,
				});

				console.table({
					userId: emp.id,
					title,
					article: content,
					instruction: form.instruction,
					model: 'gpt-4o-mini',
					temperature: 0.75,
					contentType: contentType === 0 ? 'link' : 'text',
				});

				const jobId = res?.jobId;
				dispatch(distActions.setJobStatus({ employeeId: emp.id, jobId, status: 'queued' }));
				startPollingJob(emp, jobId);
			} catch (err) {
				console.error('Generation failed:', err);
				dispatch(distActions.setJobStatus({ employeeId: emp.id, status: 'failed', error: err }));
			}
		}

		setIsGenerated(true);
	};

	// ------------------ Render helpers (visual only) ------------------
	const totalSelected = selectedEmployees.length;
	const doneCount = useMemo(
		() => Object.values(jobStatus).filter((j) => j?.status === 'completed').length,
		[jobStatus],
	);
	const progressPercent = totalSelected ? Math.round((doneCount / totalSelected) * 100) : 0;

	return (
		<>
			{/* Keep original header components */}
			<ImeanHeader initialDark={true} />
			<StickyNavigationHeader
				headerTitle={t('postDistribution.postDistributionTagName')}
				onBackClick={() => navigate('/moderation')}
				actions={
					<Space>
						<Tooltip
							title={
								t('postDistribution.hint.onlyVisualChanges') || 'UI-only redesign. Logic preserved.'
							}
						>
							<Button icon={<InfoCircleOutlined />} />
						</Tooltip>
						<Button
							type="primary"
							icon={<PlayCircleOutlined />}
							onClick={handleGenerateWithPolling}
							disabled={isGenerating || activeNow}
						>
							{isGenerating || activeNow
								? t('postDistribution.buttons.generating')
								: t('postDistribution.buttons.generate')}
						</Button>
					</Space>
				}
			/>

			<Layout style={{ minHeight: '100vh', background: 'linear-gradient(180deg,#ffffff,#faf5ff)' }}>
				<Layout.Content
					style={{ maxWidth: 1600, margin: '24px auto', width: '100%', padding: '0 16px' }}
				>
					<LoadingOverlay loading={loading}>
						<Row gutter={[5, 5]}>
							{/* LEFT COLUMN: Form & Controls */}
							<Col xs={24} lg={6}>
								<Card className="shadow-sm" bodyStyle={{ padding: 16 }} bordered>
									<Space direction="vertical" style={{ width: '100%' }} size={16}>
										<Typography.Title level={5} style={{ margin: 0 }}>
											{t('postDistribution.cards.setupTitle') || 'Distribution Setup'}
										</Typography.Title>
										<PostDistributionForm />
										<Divider style={{ margin: '8px 0' }} />
										<Space direction="vertical" style={{ width: '100%' }} size={12}>
											<Button
												block
												type="primary"
												icon={<ThunderboltOutlined />}
												onClick={handleGenerateWithPolling}
												disabled={isGenerating || activeNow}
											>
												{isGenerating || activeNow
													? t('postDistribution.buttons.generating')
													: t('postDistribution.buttons.generate')}
											</Button>

											{/* Helpful note (edge case) */}
											{!totalSelected && (
												<Alert
													type="info"
													showIcon
													message={
														t('postDistribution.hint.selectEmployees') ||
														'Select at least one employee to start generation.'
													}
												/>
											)}

											{/* Progress bar */}
											{totalSelected > 0 && (
												<div>
													<Space
														align="center"
														className="w-full"
														style={{ width: '100%', justifyContent: 'space-between' }}
													>
														<Typography.Text type="secondary">
															{t('postDistribution.progress.elapsed') || 'Elapsed'}:{' '}
															{formatTime(elapsedTime)}
														</Typography.Text>
														{lastGenerationTime !== null && (
															<Typography.Text type="secondary">
																{t('postDistribution.progress.prevRun') || 'Last run'}:{' '}
																{formatTime(lastGenerationTime)}
															</Typography.Text>
														)}
													</Space>
													<Progress
														style={{ marginTop: 8 }}
														percent={progressPercent}
														status={activeNow ? 'active' : 'normal'}
													/>
												</div>
											)}

											{/* Per-employee status list (visualized with antd List/Badge) */}
											{totalSelected > 0 && (
												<Card size="small" bordered bodyStyle={{ padding: 0 }}>
													<Collapse
														ghost
														size="small"
														defaultActiveKey={[]} // Set to [] if you want it collapsed by default
														items={[
															{
																key: '1',
																label: (
																	<Space>
																		<Typography.Text strong>
																			{t('postDistribution.selectedEmployees')} ({totalSelected})
																		</Typography.Text>
																	</Space>
																),
																children: (
																	<List
																		itemLayout="horizontal"
																		dataSource={selectedEmployees}
																		renderItem={(emp, index) => {
																			const js = jobStatus[emp.id];
																			const meta = STATUS_META[js?.status] || {
																				text: '—',
																				status: 'default',
																				icon: null,
																			};
																			return (
																				<List.Item style={{ padding: '8px 12px' }}>
																					<Space
																						size={8}
																						style={{
																							width: '100%',
																							justifyContent: 'space-between',
																						}}
																					>
																						<Space>
																							<Typography.Text type="secondary">
																								{index + 1}.
																							</Typography.Text>
																							<Typography.Text ellipsis>
																								{emp.fullName}
																							</Typography.Text>
																						</Space>
																						<Space>
																							<Badge status={meta.status} text={t(meta.text)} />
																							{meta.icon}
																						</Space>
																					</Space>
																				</List.Item>
																			);
																		}}
																	/>
																),
															},
														]}
													/>
												</Card>
											)}
										</Space>
									</Space>
								</Card>
								<Divider />
								<AutopublicationCard />
								<Divider />
							</Col>

							{/* CENTER COLUMN - Editor */}
							<Col xs={24} lg={12}>
								<Card className="shadow-sm" bodyStyle={{ padding: 16 }} bordered>
									<Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
										<Typography.Title level={5} style={{ margin: 0 }}>
											{t('postDistribution.cards.editorTitle') || 'Post Editor'}
										</Typography.Title>
									</Row>
									{/* IMPORTANT: the editor is fully preserved; only the container changed visually */}
									<PostContentEditor autopublishByEmployee={autopublishByEmployee} />
								</Card>
							</Col>
							<Col xs={24} lg={6}>
								<MediaCardSelfContained available={progressPercent === 100 ? true : false} />
								<Divider />
								<ImageGeneratorCard
									generateEndpoint={`${baseUrl}/dist/generate-image`}
									initialMediaText="Create professional social media visuals"
									onImageGenerated={(imageUrl) => {
										console.log('New image generated:', imageUrl);
										// Handle the generated image URL
									}}
								/>
							</Col>
						</Row>
					</LoadingOverlay>
				</Layout.Content>
			</Layout>
		</>
	);
};

export default DistributePostPage;
